import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import { adminHeaders } from '../lib/shop';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Phone, Mail, MapPin, Calendar, Home, Droplet } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const QuotesList = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const authed = !!sessionStorage.getItem('adminKey');

  useEffect(() => {
    if (!authed) return;
    fetchQuotes();
    // Auto refresh every 30 seconds
    const interval = setInterval(fetchQuotes, 30000);
    return () => clearInterval(interval);
  }, [authed]);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes/all`, { headers: adminHeaders() });
      setQuotes(response.data.quotes);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching quotes:', err);
      setError('Failed to load quotes');
      setLoading(false);
    }
  };

  const getCleaningTypeLabel = (type) => {
    const types = {
      'deep': 'Deep Cleaning',
      'weekly': 'Regular Weekly Cleaning',
      'fortnight': 'Regular Fortnight Cleaning',
      'renovation': 'Post Renovation Cleaning',
      'tenancy': 'Before/After Tenancy Cleaning'
    };
    return types[type] || type;
  };

  const getApplianceLabel = (id) => {
    const appliances = {
      'oven': 'Oven cleaning',
      'extractor': 'Extractor cleaning',
      'washing': 'Washing machine cleaning',
      'fridge': 'Fridge cleaning'
    };
    return appliances[id] || id;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!authed) return <Navigate to="/admin" replace />;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading quotes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <Link to="/admin" className="text-sm text-pink-600 hover:underline" data-testid="quotes-back-admin">← Shop admin</Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2 mt-2">Quote Requests</h1>
          <p className="text-gray-600">Total requests: {quotes.length}</p>
        </div>

        {quotes.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-gray-600">No quote requests yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {quotes.map((quote) => (
              <Card key={quote.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="bg-gradient-to-r from-pink-50 to-white">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl">{quote.name}</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        {formatDate(quote.created_at)}
                      </p>
                    </div>
                    <Badge className={`${
                      quote.status === 'new' ? 'bg-green-500' :
                      quote.status === 'contacted' ? 'bg-blue-500' :
                      'bg-gray-500'
                    } text-white`}>
                      {quote.status.toUpperCase()}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex items-start space-x-2">
                        <Phone className="w-5 h-5 text-pink-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Phone</p>
                          <a href={`tel:${quote.phone}`} className="text-gray-900 font-medium hover:text-pink-600">
                            {quote.phone}
                          </a>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <Mail className="w-5 h-5 text-pink-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Email</p>
                          <a href={`mailto:${quote.email}`} className="text-gray-900 font-medium hover:text-pink-600 break-all">
                            {quote.email}
                          </a>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <MapPin className="w-5 h-5 text-pink-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Address</p>
                          <p className="text-gray-900 font-medium">{quote.address}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-start space-x-2">
                        <Home className="w-5 h-5 text-pink-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Property Details</p>
                          <p className="text-gray-900 font-medium">
                            {quote.bedrooms} Bedrooms, {quote.bathrooms} Bathrooms
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <Droplet className="w-5 h-5 text-pink-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Cleaning Type</p>
                          <p className="text-gray-900 font-medium">{getCleaningTypeLabel(quote.cleaningType)}</p>
                        </div>
                      </div>
                      {quote.kitchenAppliances && quote.kitchenAppliances.length > 0 && (
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Kitchen Appliances</p>
                          <div className="flex flex-wrap gap-2">
                            {quote.kitchenAppliances.map((appliance, idx) => (
                              <Badge key={idx} variant="outline" className="bg-pink-50">
                                {getApplianceLabel(appliance)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {quote.message && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-gray-600 mb-1">Additional Message</p>
                      <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{quote.message}</p>
                    </div>
                  )}

                  {quote.images && quote.images.length > 0 && (
                    <div className="mt-4 pt-4 border-t" data-testid={`quote-images-${quote.id}`}>
                      <p className="text-sm text-gray-600 mb-2">Uploaded Pictures ({quote.images.length})</p>
                      <div className="flex flex-wrap gap-3">
                        {quote.images.map((img) => (
                          <a key={img.id} href={`${BACKEND_URL}${img.url}`} target="_blank" rel="noopener noreferrer" title={img.filename}>
                            <img
                              src={`${BACKEND_URL}${img.url}`}
                              alt={img.filename}
                              className="w-28 h-28 object-cover rounded-lg border border-gray-200 hover:opacity-90"
                            />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {quote.notification && (
                    <p className="mt-3 text-xs" data-testid={`quote-notification-${quote.id}`}>
                      {quote.notification.business_sent
                        ? <span className="text-green-600">Email notification sent</span>
                        : quote.notification.pending
                          ? <span className="text-gray-500">Email sending...</span>
                          : <span className="text-amber-600">Email not sent: {quote.notification.error}</span>}
                    </p>
                  )}

                  <div className="mt-4 pt-4 border-t flex items-center justify-between">
                    <p className="text-xs text-gray-500">Reference: {quote.id.substring(0, 8).toUpperCase()}</p>
                    <div className="flex space-x-2">
                      <a
                        href={`tel:${quote.phone}`}
                        className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 text-sm font-medium"
                      >
                        Call Now
                      </a>
                      <a
                        href={`https://wa.me/${quote.phone.replace(/\s/g, '')}?text=Hi ${quote.name}, regarding your cleaning quote request...`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm font-medium"
                      >
                        WhatsApp
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
